import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip, TextField, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { AIGenerationTypeQuery } from '../../../services/query/AIGenerationTypeQuery';
import { AIGenerationTypeCommand } from '../../../services/command/AIGenerationTypeCommand';
import { AIGenerationType } from '../../../types/AIGenerationType';
import { AIGenerationTypeDrawer } from './components/AIGenerationTypeDrawer';
import Swal from 'sweetalert2';
import './AIGenerationTypeManagement.scss';

export const AIGenerationTypeManagement = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<AIGenerationType[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchName, setSearchName] = useState('');
    const [filterIsActive, setFilterIsActive] = useState<string>('all');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<AIGenerationType | null>(null);

    useEffect(() => {
        fetchData();
    }, [currentPage, pageSize, searchName, filterIsActive]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const params: any = {
                pageNumber: currentPage + 1,
                pageSize: pageSize,
            };

            if (searchName) params.name = searchName;
            if (filterIsActive === 'active') params.isActive = true;
            if (filterIsActive === 'inactive') params.isActive = false;

            const response = await AIGenerationTypeQuery.GetAIGenerationTypes(params);
            setData(response.model?.items || []);
            setTotalCount(response.model?.totalCount || 0);
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.anErrorOccured'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(0);
    };

    const handleClearFilters = () => {
        setSearchName('');
        setFilterIsActive('all');
        setCurrentPage(0);
    };

    const handleAdd = () => {
        setSelectedType(null);
        setDrawerOpen(true);
    };

    const handleEdit = (type: AIGenerationType) => {
        setSelectedType(type);
        setDrawerOpen(true);
    };

    const handleDelete = async (id: number, name: string) => {
        const result = await Swal.fire({
            title: t('general.areYouSure'),
            text: t('aiGenerationType.deleteConfirmationText', { name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                setIsLoading(true);
                await AIGenerationTypeCommand.DeleteAIGenerationType(id);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('aiGenerationType.deleteSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchData();
            } catch {
                Swal.fire({
                    icon: 'error',
                    title: t('general.anErrorOccured'),
                    text: t('aiGenerationType.deleteError'),
                });
                setIsLoading(false);
            }
        }
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedType(null);
    };

    const handleSaveSuccess = () => {
        handleDrawerClose();
        fetchData();
    };

    const breadcrumbList = [{ label: t('general.home'), path: '/home' }, { label: t('aiGenerationType.title') }];

    const columns = [
        {
            field: 'name',
            headerName: t('aiGenerationType.name'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'code',
            headerName: t('aiGenerationType.code'),
            width: 150,
        },
        {
            field: 'description',
            headerName: t('aiGenerationType.description'),
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'systemPromptCode',
            headerName: t('aiGenerationType.systemPromptCode'),
            width: 250,
        },
        {
            field: 'isActive',
            headerName: t('aiGenerationType.isActive'),
            width: 100,
            renderCell: (params: any) => (
                <span style={{ color: params.row.isActive ? 'green' : 'red' }}>
                    {params.row.isActive ? t('general.active') : t('general.passive')}
                </span>
            ),
        },
        {
            field: 'displayOrder',
            headerName: t('aiGenerationType.displayOrder'),
            width: 100,
        },
        {
            field: 'actions',
            headerName: t('aiGenerationType.actions'),
            width: 120,
            sortable: false,
            renderCell: (params: any) => (
                <Box>
                    <Tooltip title={t('general.edit')}>
                        <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('general.delete')}>
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(params.row.id, params.row.name)}
                            color="error"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <SCUIContent>
            <>
                {isLoading && <SCUILoadingLinear />}
                <SCUIBreadCrumb items={breadcrumbList} />

                <Box className="ai-generation-type-container">
                    <Box className="filters-section">
                        <TextField
                            label={t('aiGenerationType.name')}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            size="small"
                            style={{ marginRight: 10, minWidth: 200 }}
                        />
                        <TextField
                            select
                            label={t('aiGenerationType.isActive')}
                            value={filterIsActive}
                            onChange={(e) => setFilterIsActive(e.target.value)}
                            size="small"
                            style={{ marginRight: 10, minWidth: 150 }}
                        >
                            <MenuItem value="all">{t('general.all')}</MenuItem>
                            <MenuItem value="active">{t('general.active')}</MenuItem>
                            <MenuItem value="inactive">{t('general.passive')}</MenuItem>
                        </TextField>
                        <SCUIButton
                            text={t('aiGenerationType.search')}
                            type="primary"
                            size="m"
                            onClick={handleSearch}
                            style={{ marginRight: 10 }}
                            sizeType={'fixed'}
                        />
                        <SCUIButton
                            text={t('aiGenerationType.clear')}
                            type="secondary"
                            size="m"
                            onClick={handleClearFilters}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="actions-section">
                        <SCUIButton
                            text={t('aiGenerationType.add')}
                            type="primary"
                            size="m"
                            onClick={handleAdd}
                            showStartIcon
                            startIcon={<AddIcon />}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="grid-section">
                        <SCUICustomDataGrid
                            rows={data}
                            columns={columns}
                            pageSize={pageSize}
                            onPageSizeChange={setPageSize}
                            page={currentPage}
                            onPageChange={setCurrentPage}
                            rowCount={totalCount}
                            getRowId={(row: any) => row.id}
                            paginationModeChange
                            loading={isLoading}
                        />
                    </Box>
                </Box>

                <AIGenerationTypeDrawer
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    generationType={selectedType}
                    onSaveSuccess={handleSaveSuccess}
                />
            </>
        </SCUIContent>
    );
};
